// @ts-nocheck
import type { SimplePaletteColorOptions } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";

import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

import { ScopedCssBaseline } from "components/core";

type KoruColor = {
  10: string;
  40: string;
  70: string;
  100: string;
};

const brightGreen = "#36ce7e";
const darkGreen = "#003932";
const darkRed = "#C00000";
const green: KoruColor = {
  100: "#2d805e",
  70: "#6ca68e",
  40: "#abccbf",
  10: "#eaf2ef",
};
const rose: KoruColor = {
  100: "#ff3855",
  70: "#ff7488",
  40: "#ffafbb",
  10: "#ffebee",
};
const yellow: KoruColor = {
  100: "#ffc239",
  70: "#ffd474",
  40: "#ffe7b0",
  10: "#fff9eb",
};
const orange: KoruColor = {
  100: "#f78b41",
  70: "#f9ae7a",
  40: "#fcd1b3",
  10: "#fef3ec",
};

const neutrals = {
  ice: "#f8f9fa",
  grey: "#d6d6d6",
  darkGrey: "#595959",
  lighterGrey: "#e6e6e6",
  black: "#1e1e1e",
};

const white: SimplePaletteColorOptions = {
  main: "#ffffff",
  contrastText: "#1e1e1e",
  dark: "#ffffff",
  light: "#ffffff",
};

const otherGreen: KoruColor & SimplePaletteColorOptions = {
  main: "#2D805E",
  contrastText: "#ffffff",
  dark: "rgb(31, 89, 65)",
  light: "rgb(87, 153, 126)",
  10: "#EAF2EF",
  40: "#ABCCBF",
};

const secondaryGreen: SimplePaletteColorOptions = {
  main: "#EAF2EF",
  contrastText: "#003932",
  dark: "#BAD5CA",
};

let theme = createTheme();

theme = createTheme({
  palette: {
    primary: { main: rose[100], 10: "#FFEBEE" },
    secondary: {
      main: "#003932",
      5: "#f2f5f5",
      10: "#e5ebea",
    },
    info: { main: "#007ed1", light: "#26bcf7" },
    error: { main: darkRed },
    blue: { main: "#3f769e", dark: "#2f5775", 10: "#ecf1f5" },
    darkPurple: theme.palette.augmentColor({ color: { main: "#2b296a" } }),
    green,
    brightGreen,
    darkGreen,
    darkRed,
    otherGreen,
    secondaryGreen,
    grey: { main: "#d6d6d6", light: "#eee", contrastText: "#000" },
    lighterGrey: { main: neutrals.lighterGrey, contrastText: neutrals.black },
    neutrals,
    white,
    yellow,
    orange,
    rose,
    text: {
      primary: "#1e1e1e",
      secondary: "#595959",
    },
    action: {
      border: "rgba(0, 0, 0, 0.23)",
    },
    background: {
      notWhite: "#f5f5f5",
      grey: { main: "#F8F9FA" },
      blue: {
        main: "#26bcf7",
        70: "#67D0F9",
        40: "#A8E4FC",
        10: "#E9F8FE",
      },
      purple: {
        main: "#7168ea",
        70: "#9C95F0",
        40: "#C6C3F7",
        10: "#F1F0FD",
      },
      green: {
        main: "#2D805E",
        70: "#6CA68E",
        40: "#ABCCBF",
        10: "#EAF2EF",
      },
      orange: {
        main: "#f78b41",
        70: "#F9AE7A",
        40: "#FCD1B3",
        10: "#FEF3EC",
      },
    },
    success: { main: "#36CE7E", light: "#2D805E" },
  },
  typography: {
    fontFamily: "Inter, sans-serif",
    serif: {
      fontFamily: "Abernathy, serif",
    },
    title: {
      fontSize: 24,
      fontWeight: "bold",
      lineHeight: 1.33,
      color: "#003932",
      textWrap: "balance",
    },
    subtitle: {
      fontSize: 20,
      lineHeight: 1.25,
    },
    bodyBold: {
      fontWeight: "bold",
    },
    small: {
      fontSize: 14,
    },
    info: {
      fontSize: 12,
      letterSpacing: ".01em",
    },
    button: {
      textTransform: "none",
    },
  },
});

export default createTheme(theme, {
  components: {
    MuiAlert: {
      defaultProps: {
        iconMapping: {
          success: (
            <CheckCircleOutlineIcon color="otherGreen" fontSize="inherit" />
          ),
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root .MuiAutocomplete-input": {
            padding: "3px 5px",
          },
        },
      },
    },
    MuiButton: {
      defaultProps: {
        variant: "contained",
        disableElevation: true,
        color: "otherGreen",
      },
      styleOverrides: {
        root: {
          borderRadius: 999,
          fontSize: 15,
          fontWeight: "bold",
          "&.MuiButton-text:hover": {
            textDecoration: "underline",
            textDecorationThickness: "2px",
            textUnderlineOffset: "2px",
          },
        },
        sizeMedium: {
          paddingBottom: theme.spacing(1),
          paddingLeft: theme.spacing(3),
          paddingRight: theme.spacing(3),
          paddingTop: theme.spacing(1),
        },
        sizeSmall: {
          paddingLeft: 15,
          paddingRight: 15,
          paddingTop: 7,
          paddingBottom: 7,
          lineHeight: 1.2,
          fontSize: 14,
        },
      },
      variants: [
        {
          props: { variant: "text" },
          style: {
            minWidth: 0,
            textDecoration: "underline",
            textDecorationThickness: "2px",
            textUnderlineOffset: "2px",
          },
        },
      ],
    },
    Button: {
      variants: [
        {
          props: { variant: "text", aligned: "right" },
          style: {
            marginRight: theme.spacing(-1),
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          },
        },
        {
          props: { variant: "text", aligned: "left" },
          style: {
            marginLeft: theme.spacing(-1),
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1),
          },
        },
      ],
    },
    MuiCheckbox: {
      defaultProps: {
        color: "otherGreen",
        checkedIcon: <CheckBoxRoundedIcon />,
      },
      styleOverrides: {
        root: {
          color: theme.palette.action.border,
        },
      },
    },
    MuiBadge: {
      variants: [
        {
          props: { variant: "standalone" },
          style: {
            position: "relative",
            verticalAlign: "text-bottom",
            "& .MuiBadge-badge": {
              position: "relative",
              transform: "none",
              fontWeight: "bold",
            },
          },
        },
      ],
    },
    MuiChip: {
      variants: [
        {
          props: { variant: "tag" },
          style: {
            backgroundColor: theme.palette.otherGreen[10],
            borderRadius: "4px",
            color: theme.palette.secondary.main,
            height: "28px",
            fontWeight: "bold",
            fontSize: "12px",
            "& .MuiChip-label": {
              paddingLeft: "8px",
              paddingRight: "8px",
            },
          },
        },
        {
          props: { variant: "tag", color: "error" },
          style: {
            backgroundColor: "#f9e5e5",
            color: "#9e0000",
          },
        },
        {
          props: { variant: "tag", color: "info" },
          style: {
            backgroundColor: "#e9f8fe",
            color: "#2f5575",
          },
        },
        {
          props: { variant: "tag", color: "warning" },
          style: {
            backgroundColor: "#ffe7b0",
            color: "#664700",
          },
        },
        {
          props: { variant: "tag", color: "secondary" },
          style: {
            backgroundColor: theme.palette.otherGreen.main,
            color: "#FFF",
          },
        },
        {
          props: { variant: "tag", color: "darkPurple" },
          style: {
            // eslint-disable-next-line
            backgroundColor: theme.palette.background.purple[10],
            color: theme.palette.darkPurple.main,
          },
        },
        {
          props: { variant: "tag", color: "lighterGrey" },
          style: {
            backgroundColor: "#f8f9fa",
            color: "#003932",
          },
        },
        {
          props: { variant: "tag", color: "disabled" },
          style: {
            backgroundColor: neutrals.ice,
            color: neutrals.darkGrey,
          },
        },
        {
          props: { variant: "tag", color: "otherGreen" },
          style: {
            backgroundColor: theme.palette.secondary.main,
            color: "#FFF",
          },
        },
        {
          props: { variant: "tag", color: "orange[10]" },
          style: {
            // eslint-disable-next-line
            backgroundColor: theme.palette.background.orange[10],
          },
        },
        {
          props: { variant: "tag", color: "yellow[10]" },
          style: {
            backgroundColor: theme.palette.yellow[10],
          },
        },
      ],
    },
    MuiDialog: {
      defaultProps: {
        component: ScopedCssBaseline,
      },
      styleOverrides: {
        root: {
          backgroundColor: "transparent",
          "& .MuiDialog-paper": {
            borderRadius: "12px",
          },
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "20px",
          [theme.breakpoints.down("sm")]: {
            padding: "16px",
          },
        },
        spacing: {
          "& > .MuiButton-root + .MuiButton-root": {
            marginLeft: theme.spacing(1),
          },
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          padding: "16px 24px",
          [theme.breakpoints.down("sm")]: {
            padding: "16px",
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.neutrals.grey,
        },
      },
      variants: [
        {
          props: { variant: "dashed" },
          style: {
            height: "1px",
            border: "none",
            backgroundImage: `url("data:image/svg+xml,%3csvg width='100%25' height='2' xmlns='http://www.w3.org/2000/svg'%3e%3cline x1='0' y1='1' x2='100%25' y2='1' stroke='%23D6D6D6' stroke-width='2' stroke-dasharray='8 8' stroke-linecap='round'/%3e%3c/svg%3e")`,
          },
        },
      ],
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          letterSpacing: ".01em",
        },
      },
    },
    MuiFab: {
      styleOverrides: {
        root: {
          fontSize: 15,
          "&:not(.MuiFab-circular)": {
            fontWeight: "bold",
            paddingBottom: theme.spacing(1),
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
            paddingTop: theme.spacing(1),
          },
        },
      },
    },
    MuiFormLabel: {
      variants: [
        {
          props: { variant: "standalone" },
          style: {
            color: theme.palette.text.primary,
            "&.Mui-focused:not(.Mui-error, .Mui-disabled)": {
              color: theme.palette.text.primary,
            },
          },
        },
      ],
    },
    MuiInputLabel: {
      defaultProps: {
        color: "secondary",
      },
      variants: [
        {
          props: { variant: "outlined", shrink: false },
          style: {
            transform: "translate(12px, 12px) scale(1)",
          },
        },
        {
          props: { variant: "outlined", shrink: true },
          style: {
            transform: "translate(14px, -9px) scale(.75)",
          },
        },
        {
          props: { variant: "outlined", shrink: false, size: "small" },
          style: {
            fontSize: "14px",
            transform: "translate(10px, 8px) scale(1)",
          },
        },
      ],
    },
    MuiLink: {
      defaultProps: {
        color: "info.main",
      },
      styleOverrides: {
        root: {
          // This is required to override the global Tachyons
          // `a` styling.
          "&:hover, &:focus": {
            color: theme.palette.info.main,
          },
          fontFamily: "inherit",
          verticalAlign: "initial",
          textDecorationThickness: "2px",
          textUnderlineOffset: "2px",
        },
      },
      variants: [
        {
          props: { color: "error" },
          style: {
            "&:hover, &:focus": {
              color: theme.palette.error.main,
            },
          },
        },
        {
          props: { color: "otherGreen.main" },
          style: {
            textDecorationColor: theme.palette.otherGreen.main,
            "&:hover, &:focus": {
              color: theme.palette.otherGreen.main,
            },
          },
        },
      ],
    },
    MuiOutlinedInput: {
      defaultProps: {
        color: "secondary",
      },
      styleOverrides: {
        root: {
          boxShadow: "0px 1px 0px rgba(0, 0, 0, 0.05)",
          "&.Mui-disabled": {
            backgroundColor: "#f5f5f5",
            "& .MuiInputAdornment-root": {
              color: theme.palette.text.disabled,
            },
          },
        },
        input: {
          boxSizing: "content-box !important",
          padding: "12px 14px",
          "&::placeholder": {
            color: "#9e9e9e",
            opacity: 1,
          },
          "&.Mui-disabled": {
            backgroundColor: "#f5f5f5",
          },
        },
        multiline: {
          padding: 0,
        },
      },
      variants: [
        {
          props: { size: "small" },
          style: {
            input: {
              fontSize: "14px",
              padding: "8px 10px",
            },
          },
        },
      ],
    },
    MuiRadio: {
      defaultProps: {
        color: "secondary",
      },
      styleOverrides: {
        root: {
          color: theme.palette.action.border,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        native: true,
      },
    },
    MuiSwitch: {
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          width: 48,
          height: 32,
          padding: 0,
          margin: theme.spacing(1),
          "& .MuiSwitch-switchBase": {
            padding: 2,
            "&.Mui-checked": {
              transform: "translateX(16px)",
              color: theme.palette.common.white,
              "& .MuiSwitch-thumb:before": {
                content: "''",
                position: "absolute",
                width: 27,
                height: 28,
                backgroundImage:
                  "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 27 28' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='27' height='28' rx='13.5' fill='none'/%3E%3Cpath d='m7.9891 13.631c-0.21692-0.1762-0.48567-0.2721-0.76272-0.2721-0.71627 0-1.2801 0.6253-1.2223 1.3557l0.00189 0.0235c0.02533 0.3203 0.168 0.619 0.39934 0.8366l4.2045 3.9523c0.7305 0.6866 1.8722 0.6166 2.5171-0.1545l7.5366-9.0087c0.5212-0.62318 0.4259-1.5656-0.2092-2.0662-0.5887-0.46414-1.4293-0.37703-1.9151 0.1985l-6.8629 8.1297-3.6872-2.9948z' fill='%232D805E'/%3E%3C/svg%3E\")",
              },
              "&.Mui-focusVisible .MuiSwitch-thumb:before": {
                content: "unset",
              },
              "&.Mui-disabled .MuiSwitch-thumb:before": {
                opacity: 0.5,
              },
              "& + .MuiSwitch-track": {
                opacity: 1,
                border: 0,
                backgroundColor: theme.palette.otherGreen.main,
              },
            },
            "&.Mui-focusVisible .MuiSwitch-thumb": {
              color: theme.palette.otherGreen.main,
              border: "6px solid #fff",
            },
            "&.Mui-disabled .MuiSwitch-thumb": {
              color: theme.palette.grey[100],
            },
            "&.Mui-disabled + .MuiSwitch-track": {
              opacity: 0.5,
            },
          },
          "& .MuiSwitch-thumb": {
            boxSizing: "border-box",
            width: 28,
            height: 28,
          },
          "& .MuiSwitch-track": {
            borderRadius: 999,
            backgroundColor: theme.palette.grey[500],
            opacity: 1,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: theme.palette.secondary.main,
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          boxShadow: "0px 1px 0px rgba(0, 0, 0, 0.05)",
          color: theme.palette.darkGreen,
          fontSize: "16px",
          lineHeight: "16px",
          padding: "14px 16px",
        },
      },
      variants: [
        {
          props: { color: "otherGreen" },
          style: {
            color: theme.palette.secondary.main,
            "&.Mui-selected": {
              backgroundColor: theme.palette.otherGreen[10],
              borderColor: `${theme.palette.secondary.main} !important`,
              color: theme.palette.secondary.main,
            },
          },
        },
      ],
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          title: "h1",
          subtitle: "h2",
        },
      },
      styleOverrides: {
        root: {
          "& li": {
            marginLeft: "24px",
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          "& .MuiListItemText-primary": {
            color: theme.palette.secondary.main,
            fontWeight: "bold",
          },
        },
      },
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          fontSize: "14px",
          fontWeight: "bold",
          lineHeight: "18px",
          backgroundColor: theme.palette.neutrals.ice,
          paddingTop: "8px",
          paddingBottom: "8px",
        },
      },
      variants: [
        {
          props: { color: "primary" },
          style: {
            color: theme.palette.white.main,
            backgroundColor: theme.palette.green[100],
          },
        },
      ],
    },
  },
});

/* eslint-disable @typescript-eslint/consistent-type-definitions */
declare module "@mui/material/styles" {
  interface Palette {
    brightGreen: string;
    darkGreen: string;
    darkPurple: Palette["primary"];
    green: KoruColor;
    neutrals: typeof neutrals;
    orange: KoruColor;
    otherGreen: typeof otherGreen;
    rose: KoruColor;
    white: typeof white;
    yellow: KoruColor;
  }

  interface PaletteOptions {
    brightGreen: string;
    darkGreen: string;
    green: KoruColor;
    neutrals: typeof neutrals;
    orange: KoruColor;
    otherGreen: typeof otherGreen;
    rose: KoruColor;
    white: typeof white;
    yellow: KoruColor;
  }

  interface TypographyVariants {
    bodyBold: React.CSSProperties;
    button: React.CSSProperties;
    info: React.CSSProperties;
    serif: React.CSSProperties;
    small: React.CSSProperties;
    subtitle: React.CSSProperties;
    title: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    bodyBold?: React.CSSProperties;
    button?: React.CSSProperties;
    info?: React.CSSProperties;
    serif?: React.CSSProperties;
    small?: React.CSSProperties;
    subtitle?: React.CSSProperties;
    title?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    bodyBold: true;
    button: true;
    info: true;
    serif: true;
    small: true;
    subtitle: true;
    title: true;
  }
}

declare module "@mui/material/ToggleButtonGroup" {
  interface ToggleButtonGroupPropsColorOverrides {
    otherGreen: true;
  }
}

declare module "@mui/material/Radio" {
  interface RadioPropsColorOverrides {
    otherGreen: true;
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsColorOverrides {
    otherGreen: true;
    secondaryGreen: true;
    white: true;
  }
}

declare module "@mui/material/Badge" {
  interface BadgePropsVariantOverrides {
    standalone: true;
  }

  interface BadgePropsColorOverrides {
    grey: true;
    otherGreen: true;
    success: true;
  }
}

declare module "@mui/material/Chip" {
  interface ChipPropsVariantOverrides {
    tag: true;
  }

  interface ChipPropsColorOverrides {
    darkPurple: true;
    disabled: true;
    lighterGrey: true;
    "orange[10]": true;
    "yellow[10]": true;
  }
}

declare module "@mui/material/Slider" {
  interface SliderPropsColorOverrides {
    otherGreen: true;
  }
}

declare module "@mui/material/Divider" {
  interface DividerPropsVariantOverrides {
    dashed: true;
  }
}

declare module "@mui/material/FormLabel" {
  interface FormLabelOwnProps {
    variant?: "standalone";
  }
}

declare module "@mui/material/SvgIcon" {
  interface SvgIconPropsColorOverrides {
    grey: true;
    otherGreen: true;
  }
}
